// src/pages/MarketplacePricing/MarketplacePricing.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  calculateNetProfit,
  calculateSalePrice,
  calculateBreakEven,
  compareMarketplaces,
  calculateCampaignPricing,
  DEFAULT_MARKETPLACE_PARAMS,
  formatCurrency,
  formatPercent,
  getMarketplaceList,
} from '../../utils/pricingUtils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, PieChart, Pie
} from 'recharts';
import './MarketplacePricing.css';

function MarketplacePricing() {
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Veri durumları
  const [products, setProducts] = useState([]);
  const [marketplaceSettings, setMarketplaceSettings] = useState({});
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI durumları
  const [activeTab, setActiveTab] = useState('calculator'); // calculator | comparison | scenarios | campaign
  const [selectedMarketplace, setSelectedMarketplace] = useState('trendyol');
  const [calcMode, setCalcMode] = useState('priceToProfit'); // priceToProfit | profitToPrice
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Hesaplama formu
  const [formData, setFormData] = useState({
    salePrice: '',
    targetProfitMargin: '20',
    rawMaterialCost: '',
    laborCost: '',
    packagingCost: '',
    shippingCost: '',
    subcontractingCost: '',
    adSpendRate: '',
    adSpendFixed: '',
    returnRate: '',
    vatRate: '20',
    exchangeRate: '1',
    customCosts: '',
    commissionRate: ((DEFAULT_MARKETPLACE_PARAMS.trendyol.commissionRate || 0) * 100).toString(),
    platformServiceFee: DEFAULT_MARKETPLACE_PARAMS.trendyol.fixedFee?.toString() || '0',
    productVatIncluded: 'true',
    shippingVatIncluded: 'true',
    withholdingRate: '0',
    totalProductCostInput: '', // Toplu maliyet için 
    productVatRate: '20',
    returnShippingCost: '',
  });
  
  const [formData2, setFormData2] = useState({ ...formData });
  const [calcResult2, setCalcResult2] = useState(null);

  const [costInputMode, setCostInputMode] = useState('detailed'); // 'detailed' | 'total'

  // Hesaplama sonucu
  const [calcResult, setCalcResult] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [scenarioNote, setScenarioNote] = useState('');

  // Kampanya durumları
  const [campaignType, setCampaignType] = useState('buyXpayY'); // buyXpayY | percentDiscount
  const [campaignData, setCampaignData] = useState({
    buyQuantity: '4',
    payQuantity: '3',
    discountRate: '10',
    marketplaceContribution: '2',
    estimatedSales: '100',
  });
  const [campaignResult, setCampaignResult] = useState(null);

  // Firebase verilerini yükle
  useEffect(() => {
    if (!userData?.companyId) {
      setLoading(false);
      return;
    }

    const companyId = userData.companyId;

    const unsubProducts = onSnapshot(
      query(collection(db, 'companies', companyId, 'products')),
      snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    const unsubSettings = onSnapshot(
      query(collection(db, 'companies', companyId, 'marketplaceSettings')),
      snap => {
        const settings = {};
        snap.docs.forEach(d => {
          settings[d.id] = { id: d.id, ...d.data() };
        });
        setMarketplaceSettings(settings);
      }
    );

    const unsubScenarios = onSnapshot(
      query(collection(db, 'companies', companyId, 'pricingScenarios')),
      snap => {
        setSavedScenarios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );

    return () => {
      unsubProducts();
      unsubSettings();
      unsubScenarios();
    };
  }, [userData?.companyId]);

  // Aktif pazaryeri parametreleri (kayıtlı veya varsayılan)
  const getMarketplaceParams = (mpId) => {
    return marketplaceSettings[mpId] || DEFAULT_MARKETPLACE_PARAMS[mpId] || DEFAULT_MARKETPLACE_PARAMS.trendyol;
  };

  // Ürün seçildiğinde maliyetleri doldur
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    const mp = getMarketplaceParams(selectedMarketplace);
    setFormData(prev => ({
      ...prev,
      rawMaterialCost: product.purchasePrice || product.salePrice * 0.4 || '',
      laborCost: product.laborCost || '',
      packagingCost: product.packagingCost || '',
      shippingCost: mp.shippingCost?.toString() || '',
      adSpendRate: ((mp.adSpendRate || 0) * 100).toString(),
      returnRate: ((mp.returnRate || 0) * 100).toString(),
      vatRate: ((mp.vatRate || 0.20) * 100).toString(),
      commissionRate: ((mp.commissionRate || 0) * 100).toString(),
      platformServiceFee: mp.fixedFee?.toString() || '0',
      returnShippingCost: mp.shippingCost?.toString() || '',
    }));
    if (isComparisonMode) {
      setFormData2(prev => ({
        ...prev,
        shippingCost: mp.shippingCost?.toString() || '',
        commissionRate: ((mp.commissionRate || 0) * 100).toString(),
        platformServiceFee: mp.fixedFee?.toString() || '0',
        returnShippingCost: mp.shippingCost?.toString() || '',
      }));
    }
  };

  // Pazaryeri değiştiğinde parametreleri güncelle
  const handleMarketplaceChange = (mpId) => {
    setSelectedMarketplace(mpId);
    const mp = getMarketplaceParams(mpId);
    setFormData(prev => ({
      ...prev,
      shippingCost: mp.shippingCost?.toString() || prev.shippingCost,
      adSpendRate: ((mp.adSpendRate || 0) * 100).toString(),
      returnRate: ((mp.returnRate || 0) * 100).toString(),
      vatRate: ((mp.vatRate || 0.20) * 100).toString(),
      commissionRate: ((mp.commissionRate || 0) * 100).toString(),
      platformServiceFee: mp.fixedFee?.toString() || '0',
    }));
    setCalcResult(null);
    setComparisonResult(null);
  };

  // Formu güncelle
  const updateForm = (field, value, formIndex = 1) => {
    if (formIndex === 1) {
      setFormData(prev => ({ ...prev, [field]: value }));
    } else {
      setFormData2(prev => ({ ...prev, [field]: value }));
    }
  };

  const copyMethod1To2 = () => {
    setFormData2({ ...formData });
  };

  // Hesapla butonu
  const handleCalculate = () => {
    const mp = getMarketplaceParams(selectedMarketplace);
    const isDetailed = costInputMode === 'detailed';
    const params = {
      rawMaterialCost: isDetailed ? (parseFloat(formData.rawMaterialCost) || 0) : (parseFloat(formData.totalProductCostInput) || 0),
      laborCost: isDetailed ? (parseFloat(formData.laborCost) || 0) : 0,
      packagingCost: isDetailed ? (parseFloat(formData.packagingCost) || 0) : 0,
      subcontractingCost: isDetailed ? (parseFloat(formData.subcontractingCost) || 0) : 0,
      customCosts: isDetailed ? (parseFloat(formData.customCosts) || 0) : 0,
      shippingCost: parseFloat(formData.shippingCost) || 0,
      commissionRate: (parseFloat(formData.commissionRate) || 0) / 100,
      fixedFee: parseFloat(formData.platformServiceFee) || 0,
      adSpendRate: (parseFloat(formData.adSpendRate) || 0) / 100,
      adSpendFixed: parseFloat(formData.adSpendFixed) || 0,
      returnRate: (parseFloat(formData.returnRate) || 0) / 100,
      vatRate: (parseFloat(formData.vatRate) || 20) / 100,
      productVatRate: (parseFloat(formData.productVatRate) || 20) / 100,
      exchangeRate: parseFloat(formData.exchangeRate) || 1,
      productVatIncluded: formData.productVatIncluded === 'true',
      shippingVatIncluded: formData.shippingVatIncluded === 'true',
      withholdingRate: (parseFloat(formData.withholdingRate) || 0) / 100,
    };

    if (calcMode === 'priceToProfit') {
      params.salePrice = parseFloat(formData.salePrice) || 0;
      params.returnShippingCost = parseFloat(formData.returnShippingCost) || params.shippingCost;
      const result = calculateNetProfit(params);
      setCalcResult(result);
      
      if (isComparisonMode) {
        const params2 = { ...params };
        params2.shippingCost = parseFloat(formData2.shippingCost) || 0;
        params2.commissionRate = (parseFloat(formData2.commissionRate) || 0) / 100;
        params2.fixedFee = parseFloat(formData2.platformServiceFee) || 0;
        params2.adSpendRate = (parseFloat(formData2.adSpendRate) || 0) / 100;
        params2.adSpendFixed = parseFloat(formData2.adSpendFixed) || 0;
        params2.returnRate = (parseFloat(formData2.returnRate) || 0) / 100;
        params2.returnShippingCost = parseFloat(formData2.returnShippingCost) || params2.shippingCost;
        const result2 = calculateNetProfit(params2);
        setCalcResult2(result2);
      }
    } else {
      params.targetProfitMargin = parseFloat(formData.targetProfitMargin) || 0;
      params.returnShippingCost = parseFloat(formData.returnShippingCost) || params.shippingCost;
      const result = calculateSalePrice(params);
      setCalcResult(result);

      if (isComparisonMode) {
        const params2 = { ...params };
        params2.shippingCost = parseFloat(formData2.shippingCost) || 0;
        params2.commissionRate = (parseFloat(formData2.commissionRate) || 0) / 100;
        params2.fixedFee = parseFloat(formData2.platformServiceFee) || 0;
        params2.adSpendRate = (parseFloat(formData2.adSpendRate) || 0) / 100;
        params2.adSpendFixed = parseFloat(formData2.adSpendFixed) || 0;
        params2.returnRate = (parseFloat(formData2.returnRate) || 0) / 100;
        params2.returnShippingCost = parseFloat(formData2.returnShippingCost) || params2.shippingCost;
        const result2 = calculateSalePrice(params2);
        setCalcResult2(result2);
      }
    }
  };

  // Tüm pazaryerlerinde karşılaştır
  const handleCompare = () => {
    const isDetailed = costInputMode === 'detailed';
    const productCosts = {
      rawMaterialCost: isDetailed ? (parseFloat(formData.rawMaterialCost) || 0) : (parseFloat(formData.totalProductCostInput) || 0),
      laborCost: isDetailed ? (parseFloat(formData.laborCost) || 0) : 0,
      packagingCost: isDetailed ? (parseFloat(formData.packagingCost) || 0) : 0,
      subcontractingCost: isDetailed ? (parseFloat(formData.subcontractingCost) || 0) : 0,
      exchangeRate: parseFloat(formData.exchangeRate) || 1,
      customCosts: isDetailed ? (parseFloat(formData.customCosts) || 0) : 0,
    };

    const allMps = {};
    Object.keys(DEFAULT_MARKETPLACE_PARAMS).forEach(key => {
      allMps[key] = getMarketplaceParams(key);
    });

    const salePrice = calcMode === 'priceToProfit' ? (parseFloat(formData.salePrice) || 0) : null;
    const targetMargin = calcMode === 'profitToPrice' ? (parseFloat(formData.targetProfitMargin) || 0) : null;

    const result = compareMarketplaces(productCosts, allMps, salePrice, targetMargin);
    setComparisonResult(result);
    setActiveTab('comparison');
  };

  // Senaryo kaydet
  const handleSaveScenario = async () => {
    if (!userData?.companyId || !calcResult) return;

    const scenarioData = {
      productId: selectedProduct?.id || null,
      productName: selectedProduct?.name || 'Manuel Hesaplama',
      marketplace: selectedMarketplace,
      marketplaceName: getMarketplaceParams(selectedMarketplace).name,
      formData: { ...formData },
      result: { ...calcResult },
      mode: calcMode,
      notes: scenarioNote,
      createdAt: new Date(),
      createdBy: userData.email || userData.uid,
    };

    const scenarioId = `${Date.now()}_${selectedMarketplace}`;
    await setDoc(
      doc(db, 'companies', userData.companyId, 'pricingScenarios', scenarioId),
      scenarioData
    );

    setSaveModalOpen(false);
    setScenarioNote('');
    setActiveTab('scenarios');
  };

  // Senaryo sil
  const handleDeleteScenario = async (scenarioId) => {
    if (!userData?.companyId) return;
    await deleteDoc(doc(db, 'companies', userData.companyId, 'pricingScenarios', scenarioId));
  };

  // Filtrelenmiş ürünler
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products.filter(p => !p.isRawMaterial);
    const lower = searchTerm.toLowerCase();
    return products.filter(p =>
      !p.isRawMaterial &&
      (p.name?.toLowerCase().includes(lower) ||
       p.code?.toLowerCase().includes(lower) ||
       p.barcode?.includes(searchTerm))
    );
  }, [products, searchTerm]);

  // Grafik verisi
  const chartData = useMemo(() => {
    if (!comparisonResult) return [];
    return Object.entries(comparisonResult)
      .filter(([key]) => !key.startsWith('_'))
      .map(([key, data]) => ({
        name: data.marketplace?.name || key,
        netProfit: data.netProfit || 0,
        commissionAmount: data.commissionAmount || 0,
        adCost: data.adCost || 0,
        breakEvenPrice: data.breakEvenPrice || 0,
        color: data.marketplace?.color || '#06C167',
        isBest: comparisonResult._bestMarketplace === key,
      }));
  }, [comparisonResult]);

  const marketplaces = getMarketplaceList();
  const activeMP = getMarketplaceParams(selectedMarketplace);

  if (loading) {
    return (
      <div className="mp-container">
        <div className="mp-loading">
          <div className="mp-spinner" />
          <p style={{ color: 'var(--text-muted)' }}>Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-container">
      {/* Header */}
      <div className="mp-header">
        <div className="mp-header-left">
          <h1>Pazaryeri Fiyatlandırma</h1>
          <p>Ürün bazlı satış fiyatı belirleme ve net kâr hesaplama</p>
        </div>
        <div className="mp-header-actions">
          <button
            className="mp-btn mp-btn-secondary"
            onClick={() => navigate('/pazaryeri-fiyatlama/toplu')}
          >
            📑 Toplu Fiyatlama
          </button>
        </div>
      </div>

      {/* Pazaryeri Kartları */}
      <div className="mp-summary-grid">
        {marketplaces.map((mp, i) => (
          <div
            key={mp.id}
            className={`mp-summary-card ${mp.id} mp-animate-in ${selectedMarketplace === mp.id ? 'active' : ''}`}
            onClick={() => handleMarketplaceChange(mp.id)}
          >
            <div className="mp-summary-icon">{mp.icon}</div>
            <div className="mp-summary-name">{mp.name}</div>
            <div className="mp-summary-detail">
              Komisyon: {formatPercent(mp.commissionRate * 100)}
            </div>
            <div className="mp-summary-value">
              {formatPercent(mp.commissionRate * 100)}
            </div>
            <div className="mp-summary-label">Komisyon Oranı</div>
          </div>
        ))}
      </div>

      {/* Tab Navigasyon */}
      <div className="mp-tabs">
        <button
          className={`mp-tab ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          🧮 Fiyat Hesapla
        </button>
        <button
          className={`mp-tab ${activeTab === 'comparison' ? 'active' : ''}`}
          onClick={() => setActiveTab('comparison')}
        >
          📊 Karşılaştırma
        </button>
        <button
          className={`mp-tab ${activeTab === 'scenarios' ? 'active' : ''}`}
          onClick={() => setActiveTab('scenarios')}
        >
          💾 Kayıtlı Senaryolar ({savedScenarios.length})
        </button>
        <button
          className={`mp-tab ${activeTab === 'campaign' ? 'active' : ''}`}
          onClick={() => setActiveTab('campaign')}
        >
          🎪 Kampanya
        </button>
      </div>

      {/* TAB: Hesaplama */}
      {activeTab === 'calculator' && (
        <div className="mp-calc-panel mp-animate-in">
          <div className="mp-calc-header">
            <div>
              <h3 className="mp-card-title" style={{ margin: 0 }}>
                {activeMP.icon} {activeMP.name} — Fiyat Hesaplama
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                Varsayılan Komisyon: {formatPercent(activeMP.commissionRate * 100)} | 
                Ödeme: {activeMP.paymentDelay} gün
              </p>
            </div>
            <button
              className="mp-btn mp-btn-primary mp-btn-sm"
              onClick={handleCompare}
              disabled={!formData.rawMaterialCost}
            >
              📊 Tümüyle Karşılaştır
            </button>
          </div>

          <div className="mp-calc-body">
            {/* Mod Seçimi */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
              <div className="mp-mode-toggle">
                <button
                  className={`mp-mode-btn ${calcMode === 'priceToProfit' ? 'active' : ''}`}
                  onClick={() => setCalcMode('priceToProfit')}
                >
                  💰 Fiyat → Kâr
                </button>
                <button
                  className={`mp-mode-btn ${calcMode === 'profitToPrice' ? 'active' : ''}`}
                  onClick={() => setCalcMode('profitToPrice')}
                >
                  🎯 Hedef Kâr → Fiyat
                </button>
              </div>

              <div className="mp-mode-toggle">
                <button
                  className={`mp-mode-btn ${!isComparisonMode ? 'active' : ''}`}
                  onClick={() => setIsComparisonMode(false)}
                >
                  👤 Tekli
                </button>
                <button
                  className={`mp-mode-btn ${isComparisonMode ? 'active' : ''}`}
                  onClick={() => setIsComparisonMode(true)}
                >
                  👥 Karşılaştırma
                </button>
              </div>
            </div>

            <div className="mp-calc-grid">
              {/* Sol: Form */}
              <div>
                {/* Ürün Seçimi */}
                <div className="mp-form-group">
                  <label className="mp-form-label">Ürün Seçin (Opsiyonel)</label>
                  <select
                    className="mp-form-select"
                    value={selectedProduct?.id || ''}
                    onChange={(e) => {
                      const prod = products.find(p => p.id === e.target.value);
                      if (prod) handleProductSelect(prod);
                    }}
                  >
                    <option value="">— Manuel Giriş —</option>
                    {filteredProducts.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.code ? `(${p.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Fiyat veya Hedef Kâr */}
                {calcMode === 'priceToProfit' ? (
                  <div className="mp-form-group">
                    <label className="mp-form-label">Satış Fiyatı (KDV Dahil) *</label>
                    <div className="mp-form-input-group">
                      <input
                        type="number"
                        className="mp-form-input"
                        placeholder="0.00"
                        value={formData.salePrice}
                        onChange={(e) => updateForm('salePrice', e.target.value)}
                      />
                      <span className="mp-form-suffix">₺</span>
                    </div>
                  </div>
                ) : (
                  <div className="mp-form-group">
                    <label className="mp-form-label">Hedef Kâr Marjı *</label>
                    <div className="mp-form-input-group">
                      <input
                        type="number"
                        className="mp-form-input"
                        placeholder="20"
                        value={formData.targetProfitMargin}
                        onChange={(e) => updateForm('targetProfitMargin', e.target.value)}
                      />
                      <span className="mp-form-suffix">%</span>
                    </div>
                  </div>
                )}

                {/* Platform Parametreleri */}
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', margin: '20px 0 12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Platform Parametreleri
                </div>

                 <div style={{ display: 'grid', gridTemplateColumns: isComparisonMode ? '1fr 1fr' : '1fr', gap: '20px' }}>
                  {/* SÖL / YÖNTEM 1 */}
                  <div style={{ background: isComparisonMode ? 'rgba(99, 102, 241, 0.02)' : 'none', padding: isComparisonMode ? '16px' : '0', borderRadius: '12px', border: isComparisonMode ? '1px solid var(--border-color)' : 'none' }}>
                    {isComparisonMode && <div style={{ fontWeight: '700', color: 'var(--text-accent)', marginBottom: '16px', fontSize: '14px' }}>🏁 Yöntem 1</div>}
                    <div className="mp-form-row">
                      <div className="mp-form-group">
                        <label className="mp-form-label">Komisyon Oranı *</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="20"
                            value={formData.commissionRate}
                            onChange={(e) => updateForm('commissionRate', e.target.value, 1)}
                          />
                          <span className="mp-form-suffix">%</span>
                        </div>
                      </div>
                      <div className="mp-form-group">
                        <label className="mp-form-label">Platform Hizmet Bedeli</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="0.00"
                            value={formData.platformServiceFee}
                            onChange={(e) => updateForm('platformServiceFee', e.target.value, 1)}
                          />
                          <span className="mp-form-suffix">₺</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SAĞ / YÖNTEM 2 */}
                  {isComparisonMode && (
                    <div style={{ background: 'rgba(5, 150, 105, 0.02)', padding: '16px', borderRadius: '12px', border: '1px solid #05966933' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div style={{ fontWeight: '700', color: '#059669', fontSize: '14px' }}>🏁 Yöntem 2</div>
                        <button className="mp-btn mp-btn-sm" style={{ fontSize: '11px', padding: '2px 8px' }} onClick={copyMethod1To2}>
                          Yöntem 1'den Kopyala
                        </button>
                      </div>
                      <div className="mp-form-row">
                        <div className="mp-form-group">
                          <label className="mp-form-label">Komisyon Oranı *</label>
                          <div className="mp-form-input-group">
                            <input
                              type="number"
                              className="mp-form-input"
                              placeholder="20"
                              value={formData2.commissionRate}
                              onChange={(e) => updateForm('commissionRate', e.target.value, 2)}
                            />
                            <span className="mp-form-suffix">%</span>
                          </div>
                        </div>
                        <div className="mp-form-group">
                          <label className="mp-form-label">Platform Hizmet Bedeli</label>
                          <div className="mp-form-input-group">
                            <input
                              type="number"
                              className="mp-form-input"
                              placeholder="0.00"
                              value={formData2.platformServiceFee}
                              onChange={(e) => updateForm('platformServiceFee', e.target.value, 2)}
                            />
                            <span className="mp-form-suffix">₺</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Maliyet Kalemleri */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', margin: '20px 0 12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Ürün Maliyeti
                    </div>
                    <div className="mp-mode-toggle" style={{ transform: 'scale(0.85)', transformOrigin: 'left center', margin: 0 }}>
                      <button 
                        className={`mp-mode-btn ${costInputMode === 'total' ? 'active' : ''}`}
                        onClick={() => setCostInputMode('total')}
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        Toplam
                      </button>
                      <button 
                        className={`mp-mode-btn ${costInputMode === 'detailed' ? 'active' : ''}`}
                        onClick={() => setCostInputMode('detailed')}
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        Detaylı
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Maliyetler KDV:</label>
                      <select
                        className="mp-form-select"
                        style={{ padding: '4px 24px 4px 8px', fontSize: '12px', height: 'auto', minHeight: '26px' }}
                        value={formData.productVatIncluded}
                        onChange={(e) => updateForm('productVatIncluded', e.target.value)}
                      >
                        <option value="true">Dahil</option>
                        <option value="false">Hariç</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Ürün KDV %:</label>
                      <div className="mp-form-input-group" style={{ width: '60px', minHeight: '26px' }}>
                        <input
                          type="number"
                          className="mp-form-input"
                          style={{ padding: '4px 8px', fontSize: '12px', height: 'auto', minHeight: '26px' }}
                          value={formData.productVatRate}
                          onChange={(e) => updateForm('productVatRate', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {costInputMode === 'total' ? (
                  <div className="mp-form-row">
                    <div className="mp-form-group">
                      <label className="mp-form-label">Toplam Ürün Maliyeti *</label>
                      <div className="mp-form-input-group">
                        <input
                          type="number"
                          className="mp-form-input"
                          placeholder="0.00"
                          value={formData.totalProductCostInput}
                          onChange={(e) => updateForm('totalProductCostInput', e.target.value)}
                        />
                        <span className="mp-form-suffix">₺</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                        Ürünün size olan toplam çıplak maliyeti (üretim/alım bedeli).
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mp-form-row">
                      <div className="mp-form-group">
                        <label className="mp-form-label">Hammadde / Ürün Bedeli *</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="0.00"
                            value={formData.rawMaterialCost}
                            onChange={(e) => updateForm('rawMaterialCost', e.target.value)}
                          />
                          <span className="mp-form-suffix">₺</span>
                        </div>
                      </div>
                      <div className="mp-form-group">
                        <label className="mp-form-label">İşçilik Maliyeti</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="0.00"
                            value={formData.laborCost}
                            onChange={(e) => updateForm('laborCost', e.target.value)}
                          />
                          <span className="mp-form-suffix">₺</span>
                        </div>
                      </div>
                    </div>

                    <div className="mp-form-row">
                      <div className="mp-form-group">
                        <label className="mp-form-label">Paketleme Maliyeti</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="0.00"
                            value={formData.packagingCost}
                            onChange={(e) => updateForm('packagingCost', e.target.value)}
                          />
                          <span className="mp-form-suffix">₺</span>
                        </div>
                      </div>
                      <div className="mp-form-group">
                        <label className="mp-form-label">Fason / Ek Maliyetler</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="0.00"
                            value={formData.subcontractingCost}
                            onChange={(e) => updateForm('subcontractingCost', e.target.value)}
                          />
                          <span className="mp-form-suffix">₺</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                 <div style={{ display: 'grid', gridTemplateColumns: isComparisonMode ? '1fr 1fr' : '1fr', gap: '20px' }}>
                  <div style={{ background: isComparisonMode ? 'rgba(99, 102, 241, 0.02)' : 'none', padding: isComparisonMode ? '16px' : '0', borderRadius: '12px', border: isComparisonMode ? '1px solid var(--border-color)' : 'none' }}>
                    <div className="mp-form-row">
                      <div className="mp-form-group">
                        <label className="mp-form-label">Gidiş Kargo</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            value={formData.shippingCost}
                            onChange={(e) => updateForm('shippingCost', e.target.value, 1)}
                          />
                          <span className="mp-form-suffix">₺</span>
                        </div>
                      </div>
                      <div className="mp-form-group">
                        <label className="mp-form-label">İade Kargo</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            value={formData.returnShippingCost}
                            onChange={(e) => updateForm('returnShippingCost', e.target.value, 1)}
                          />
                          <span className="mp-form-suffix">₺</span>
                        </div>
                      </div>
                    </div>
                    <div className="mp-form-row">
                      <div className="mp-form-group">
                        <label className="mp-form-label">Reklam Oranı</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            value={formData.adSpendRate}
                            onChange={(e) => updateForm('adSpendRate', e.target.value, 1)}
                          />
                          <span className="mp-form-suffix">%</span>
                        </div>
                      </div>
                      <div className="mp-form-group">
                        <label className="mp-form-label">İade Oranı</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            value={formData.returnRate}
                            onChange={(e) => updateForm('returnRate', e.target.value, 1)}
                          />
                          <span className="mp-form-suffix">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isComparisonMode && (
                    <div style={{ background: 'rgba(5, 150, 105, 0.02)', padding: '16px', borderRadius: '12px', border: '1px solid #05966933' }}>
                      <div className="mp-form-row">
                        <div className="mp-form-group">
                          <label className="mp-form-label">Gidiş Kargo</label>
                          <div className="mp-form-input-group">
                            <input
                              type="number"
                              className="mp-form-input"
                              value={formData2.shippingCost}
                              onChange={(e) => updateForm('shippingCost', e.target.value, 2)}
                            />
                            <span className="mp-form-suffix">₺</span>
                          </div>
                        </div>
                        <div className="mp-form-group">
                          <label className="mp-form-label">İade Kargo</label>
                          <div className="mp-form-input-group">
                            <input
                              type="number"
                              className="mp-form-input"
                              value={formData2.returnShippingCost}
                              onChange={(e) => updateForm('returnShippingCost', e.target.value, 2)}
                            />
                            <span className="mp-form-suffix">₺</span>
                          </div>
                        </div>
                      </div>
                      <div className="mp-form-row">
                        <div className="mp-form-group">
                          <label className="mp-form-label">Reklam Oranı</label>
                          <div className="mp-form-input-group">
                            <input
                              type="number"
                              className="mp-form-input"
                              value={formData2.adSpendRate}
                              onChange={(e) => updateForm('adSpendRate', e.target.value, 2)}
                            />
                            <span className="mp-form-suffix">%</span>
                          </div>
                        </div>
                        <div className="mp-form-group">
                          <label className="mp-form-label">İade Oranı</label>
                          <div className="mp-form-input-group">
                            <input
                              type="number"
                              className="mp-form-input"
                              value={formData2.returnRate}
                              onChange={(e) => updateForm('returnRate', e.target.value, 2)}
                            />
                            <span className="mp-form-suffix">%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mp-form-row">
                  <div className="mp-form-group">
                    <label className="mp-form-label">KDV Oranı</label>
                    <div className="mp-form-input-group">
                      <input
                        type="number"
                        className="mp-form-input"
                        placeholder="20"
                        value={formData.vatRate}
                        onChange={(e) => updateForm('vatRate', e.target.value)}
                      />
                      <span className="mp-form-suffix">%</span>
                    </div>
                  </div>
                  <div className="mp-form-group">
                    <label className="mp-form-label">Kur Çarpanı</label>
                    <div className="mp-form-input-group">
                      <input
                        type="number"
                        className="mp-form-input"
                        placeholder="1"
                        value={formData.exchangeRate}
                        onChange={(e) => updateForm('exchangeRate', e.target.value)}
                      />
                      <span className="mp-form-suffix">×</span>
                    </div>
                  </div>
                </div>

                <div className="mp-form-row">
                  <div className="mp-form-group">
                    <label className="mp-form-label">Stopaj Oranı</label>
                    <div className="mp-form-input-group">
                      <input
                        type="number"
                        className="mp-form-input"
                        placeholder="0"
                        value={formData.withholdingRate}
                        onChange={(e) => updateForm('withholdingRate', e.target.value)}
                      />
                      <span className="mp-form-suffix">%</span>
                    </div>
                  </div>
                  <div className="mp-form-group">
                  </div>
                </div>

                {/* Hesapla Butonu */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    className="mp-btn mp-btn-primary"
                    onClick={handleCalculate}
                    style={{ flex: 1 }}
                  >
                    {calcMode === 'priceToProfit' ? '💰 Kâr Hesapla' : '🎯 Fiyat Hesapla'}
                  </button>
                  {calcResult && (
                    <button
                      className="mp-btn mp-btn-secondary"
                      onClick={() => setSaveModalOpen(true)}
                    >
                      💾 Kaydet
                    </button>
                  )}
                </div>
              </div>

              {/* Sağ: Sonuçlar */}
              <div>
                {(calcResult || calcResult2) ? (
                  <div className="mp-result-panel mp-animate-in" style={{ maxWidth: isComparisonMode ? '100%' : '500px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isComparisonMode ? '1fr 1fr' : '1fr', gap: isComparisonMode ? '24px' : '0' }}>
                      
                      {/* SÜTUN 1: Yöntem 1 */}
                      <div style={{ position: 'relative', opacity: (isComparisonMode && calcResult2 && calcResult.netProfit < calcResult2.netProfit) ? 0.8 : 1 }}>
                        <h4 className="mp-result-title">
                          📋 {isComparisonMode ? 'Yöntem 1' : 'Hesaplama Sonuçları'}
                          <span className={`mp-profit-indicator ${calcResult.netProfit > 0 ? 'profit' : calcResult.netProfit === 0 ? 'breakeven' : 'loss'}`}>
                            {calcResult.netProfit > 0 ? '✅ Kârlı' : calcResult.netProfit === 0 ? '⚖️ Başa-Baş' : '❌ Zararda'}
                          </span>
                        </h4>
                        
                        {isComparisonMode && calcResult2 && calcResult.netProfit > calcResult2.netProfit && (
                          <div style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#059669', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', zIndex: 10 }}>
                            🏆 DAHA KÂRLI
                          </div>
                        )}

                        {/* Ana fiyat bilgisi */}
                        <div className="mp-result-row mp-result-total" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '12px' }}>
                          <span className="mp-result-label" style={{ fontSize: '13px', fontWeight: '600' }}>Satış (Dahil)</span>
                          <span className="mp-result-value" style={{ fontSize: '16px' }}>{formatCurrency(calcResult.salePrice)}</span>
                        </div>
                        <div className="mp-result-row" style={{ marginTop: '-8px', marginBottom: '12px' }}>
                          <span className="mp-result-label" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Satış (Hariç)</span>
                          <span className="mp-result-value" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{formatCurrency(calcResult.priceExVat)}</span>
                        </div>

                        <div className="mp-result-row" style={{ marginBottom: '4px' }}>
                          <span className="mp-result-label">📦 Ürün Maliyeti</span>
                          <span className="mp-result-value negative">−{formatCurrency(calcResult.totalProductCost)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '12px', paddingLeft: '12px' }}>
                          <span>Net: {formatCurrency(calcResult.productCostExVat)}</span>
                          <span>KDV: {formatCurrency(calcResult.productVat)}</span>
                        </div>
                        
                        <div className="mp-result-row" style={{ marginBottom: '4px' }}>
                          <span className="mp-result-label">🏷️ Komisyon ({formatPercent(parseFloat(formData.commissionRate) || 0)})</span>
                          <span className="mp-result-value negative">−{formatCurrency(calcResult.commissionAmount)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '12px', paddingLeft: '12px' }}>
                          <span>Net: {formatCurrency(calcResult.commissionExVat)}</span>
                          <span>KDV: {formatCurrency(calcResult.commissionVat)}</span>
                        </div>

                        <div className="mp-result-row" style={{ marginBottom: '4px' }}>
                          <span className="mp-result-label">🚚 Kargo</span>
                          <span className="mp-result-value negative">−{formatCurrency(calcResult.shippingExpense)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '12px', paddingLeft: '12px' }}>
                          <span>Net: {formatCurrency(calcResult.shippingExVat)}</span>
                          <span>KDV: {formatCurrency(calcResult.shippingVat)}</span>
                        </div>

                        <div className="mp-result-row" style={{ marginBottom: '4px' }}>
                          <span className="mp-result-label">📢 Reklam</span>
                          <span className="mp-result-value negative">−{formatCurrency(calcResult.adCost)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '12px', paddingLeft: '12px' }}>
                          <span>Net: {formatCurrency(calcResult.adExVat)}</span>
                          <span>KDV: {formatCurrency(calcResult.adVat)}</span>
                        </div>

                        <div className="mp-result-row">
                          <span className="mp-result-label">↩️ İade Etkisi</span>
                          <span className="mp-result-value negative">−{formatCurrency(calcResult.returnImpact)}</span>
                        </div>

                        <div style={{ borderTop: '1px solid var(--border-color)', margin: '12px 0', paddingTop: '12px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', marginBottom: '8px' }}>📊 KDV DURUMU</div>
                          <div className="mp-result-row" style={{ fontSize: '11px' }}>
                            <span className="mp-result-label">Alınan (Satış KDV)</span>
                            <span className="mp-result-value negative">−{formatCurrency(calcResult.vatAmount)}</span>
                          </div>
                          <div className="mp-result-row" style={{ fontSize: '11px', borderBottom: 'none' }}>
                            <span className="mp-result-label">Ödenen (Mahsup)</span>
                            <span className="mp-result-value positive">+{formatCurrency(calcResult.totalRecoverableVat)}</span>
                          </div>
                          <div className="mp-result-row" style={{ background: 'rgba(99, 102, 241, 0.05)', padding: '6px 8px', borderRadius: '6px', marginTop: '8px' }}>
                            <span className="mp-result-label" style={{ fontWeight: '700', fontSize: '11px' }}>NET KDV</span>
                            <span className="mp-result-value" style={{ color: calcResult.netVatToPay > 0 ? '#dc2626' : '#059669', fontSize: '12px', fontWeight: '700' }}>
                              {calcResult.netVatToPay > 0 ? '−' : '+'}{formatCurrency(Math.abs(calcResult.netVatToPay))}
                            </span>
                          </div>
                        </div>

                        <div className="mp-result-row mp-result-total" style={{ 
                          marginTop: '16px', 
                          padding: '16px', 
                          borderTop: '2px solid #06C167',
                          background: 'rgba(99, 102, 241, 0.03)',
                          borderRadius: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '12px', color: 'var(--text-secondary)' }}>
                            <span>KDV Öncesi Kar</span>
                            <span>{formatCurrency(calcResult.netProfit + calcResult.netVatToPay)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '12px', color: calcResult.netVatToPay > 0 ? '#dc2626' : '#059669' }}>
                            <span>KDV Dengesi Etkisi</span>
                            <span>{calcResult.netVatToPay > 0 ? '−' : '+'}{formatCurrency(Math.abs(calcResult.netVatToPay))}</span>
                          </div>
                          <div style={{ borderTop: '1px dashed rgba(99, 102, 241, 0.2)', margin: '4px 0' }}></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span style={{ fontSize: '14px', fontWeight: '800' }}>💎 NET KÂR</span>
                            <span style={{ fontSize: '24px', fontWeight: '800', color: calcResult.netProfit > 0 ? '#059669' : calcResult.netProfit === 0 ? '#d97706' : '#dc2626' }}>
                              {formatCurrency(calcResult.netProfit)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <span className="mp-badge mp-badge-blue" style={{ fontSize: '10px' }}>Marj: {formatPercent(calcResult.profitMargin)}</span>
                          </div>
                        </div>
                      </div>

                      {/* SÜTUN 2: Yöntem 2 */}
                      {isComparisonMode && calcResult2 && (
                        <div style={{ position: 'relative', borderLeft: '1px solid var(--border-color)', paddingLeft: '24px', opacity: (calcResult2.netProfit < calcResult.netProfit) ? 0.8 : 1 }}>
                          <h4 className="mp-result-title">
                            📋 Yöntem 2
                            <span className={`mp-profit-indicator ${calcResult2.netProfit > 0 ? 'profit' : calcResult2.netProfit === 0 ? 'breakeven' : 'loss'}`}>
                              {calcResult2.netProfit > 0 ? '✅ Kârlı' : calcResult2.netProfit === 0 ? '⚖️ Başa-Baş' : '❌ Zararda'}
                            </span>
                          </h4>
                          
                          {calcResult2.netProfit > calcResult.netProfit && (
                            <div style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#059669', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', zIndex: 10 }}>
                              🏆 DAHA KÂRLI
                            </div>
                          )}

                          {/* Ana fiyat bilgisi */}
                          <div className="mp-result-row mp-result-total" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '12px' }}>
                            <span className="mp-result-label" style={{ fontSize: '13px', fontWeight: '600' }}>Satış (Dahil)</span>
                            <span className="mp-result-value" style={{ fontSize: '16px' }}>{formatCurrency(calcResult2.salePrice)}</span>
                          </div>
                          <div className="mp-result-row" style={{ marginTop: '-8px', marginBottom: '12px' }}>
                            <span className="mp-result-label" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Satış (Hariç)</span>
                            <span className="mp-result-value" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{formatCurrency(calcResult2.priceExVat)}</span>
                          </div>

                          <div className="mp-result-row" style={{ marginBottom: '4px' }}>
                            <span className="mp-result-label">📦 Ürün Maliyeti</span>
                            <span className="mp-result-value negative">−{formatCurrency(calcResult2.totalProductCost)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '12px', paddingLeft: '12px' }}>
                            <span>Net: {formatCurrency(calcResult2.productCostExVat)}</span>
                            <span>KDV: {formatCurrency(calcResult2.productVat)}</span>
                          </div>

                          <div className="mp-result-row" style={{ marginBottom: '4px' }}>
                            <span className="mp-result-label">🏷️ Komisyon ({formatPercent(parseFloat(formData2.commissionRate) || 0)})</span>
                            <span className="mp-result-value negative">−{formatCurrency(calcResult2.commissionAmount)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '12px', paddingLeft: '12px' }}>
                            <span>Net: {formatCurrency(calcResult2.commissionExVat)}</span>
                            <span>KDV: {formatCurrency(calcResult2.commissionVat)}</span>
                          </div>

                          <div className="mp-result-row" style={{ marginBottom: '4px' }}>
                            <span className="mp-result-label">🚚 Kargo</span>
                            <span className="mp-result-value negative">−{formatCurrency(calcResult2.shippingExpense)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '12px', paddingLeft: '12px' }}>
                            <span>Net: {formatCurrency(calcResult2.shippingExVat)}</span>
                            <span>KDV: {formatCurrency(calcResult2.shippingVat)}</span>
                          </div>

                          <div className="mp-result-row" style={{ marginBottom: '4px' }}>
                            <span className="mp-result-label">📢 Reklam</span>
                            <span className="mp-result-value negative">−{formatCurrency(calcResult2.adCost)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '12px', paddingLeft: '12px' }}>
                            <span>Net: {formatCurrency(calcResult2.adExVat)}</span>
                            <span>KDV: {formatCurrency(calcResult2.adVat)}</span>
                          </div>

                          <div className="mp-result-row">
                            <span className="mp-result-label">↩️ İade Etkisi</span>
                            <span className="mp-result-value negative">−{formatCurrency(calcResult2.returnImpact)}</span>
                          </div>

                          <div style={{ borderTop: '1px solid var(--border-color)', margin: '12px 0', paddingTop: '12px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', marginBottom: '8px' }}>📊 KDV DURUMU</div>
                            <div className="mp-result-row" style={{ fontSize: '11px' }}>
                              <span className="mp-result-label">Alınan (Satış KDV)</span>
                              <span className="mp-result-value negative">−{formatCurrency(calcResult2.vatAmount)}</span>
                            </div>
                            <div className="mp-result-row" style={{ fontSize: '11px', borderBottom: 'none' }}>
                              <span className="mp-result-label">Ödenen (Mahsup)</span>
                              <span className="mp-result-value positive">+{formatCurrency(calcResult2.totalRecoverableVat)}</span>
                            </div>
                            <div className="mp-result-row" style={{ background: 'rgba(5, 150, 105, 0.05)', padding: '6px 8px', borderRadius: '6px', marginTop: '8px' }}>
                              <span className="mp-result-label" style={{ fontWeight: '700', fontSize: '11px' }}>NET KDV</span>
                              <span className="mp-result-value" style={{ color: calcResult2.netVatToPay > 0 ? '#dc2626' : '#059669', fontSize: '12px', fontWeight: '700' }}>
                                {calcResult2.netVatToPay > 0 ? '−' : '+'}{formatCurrency(Math.abs(calcResult2.netVatToPay))}
                              </span>
                            </div>
                          </div>

                          <div className="mp-result-row mp-result-total" style={{ 
                            marginTop: '16px', 
                            padding: '16px', 
                            borderTop: '2px solid #059669',
                            background: 'rgba(5, 150, 105, 0.03)',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '11px', color: 'var(--text-secondary)' }}>
                              <span>KDV Öncesi Kar</span>
                              <span>{formatCurrency(calcResult2.netProfit + calcResult2.netVatToPay)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '11px', color: calcResult2.netVatToPay > 0 ? '#dc2626' : '#059669' }}>
                              <span>KDV Dengesi Etkisi</span>
                              <span>{calcResult2.netVatToPay > 0 ? '−' : '+'}{formatCurrency(Math.abs(calcResult2.netVatToPay))}</span>
                            </div>
                            <div style={{ borderTop: '1px dashed rgba(5, 150, 105, 0.2)', margin: '4px 0' }}></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <span style={{ fontSize: '13px', fontWeight: '800' }}>💎 NET KÂR</span>
                              <span style={{ fontSize: '22px', fontWeight: '800', color: calcResult2.netProfit > 0 ? '#059669' : calcResult2.netProfit === 0 ? '#d97706' : '#dc2626' }}>
                                {formatCurrency(calcResult2.netProfit)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                              <span className="mp-badge mp-badge-blue" style={{ fontSize: '10px' }}>Marj: {formatPercent(calcResult2.profitMargin)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Karşılaştırma Özeti */}
                    {isComparisonMode && calcResult && calcResult2 && (
                      <div style={{ marginTop: '20px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '10px', fontSize: '13px', fontWeight: '600', textAlign: 'center', border: '1px solid var(--border-color)', color: 'var(--text-accent)' }}>
                        {calcResult.netProfit > calcResult2.netProfit 
                          ? `💡 Yöntem 1, Yöntem 2'den ${formatCurrency(calcResult.netProfit - calcResult2.netProfit)} daha kârlı.`
                          : calcResult2.netProfit > calcResult.netProfit
                            ? `💡 Yöntem 2, Yöntem 1'den ${formatCurrency(calcResult2.netProfit - calcResult.netProfit)} daha kârlı.`
                            : "💡 Her iki yöntem de aynı kârlılığa sahip."}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mp-result-panel" style={{ textAlign: 'center', padding: '48px 24px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🧮</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Hesaplama Bekliyor
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '280px', margin: '0 auto' }}>
                      Soldaki formu doldurup "Hesapla" butonuna tıklayın. Sonuçlar burada gösterilecek.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB: Karşılaştırma */}
      {activeTab === 'comparison' && (
        <div className="mp-animate-in">
          {comparisonResult ? (
            <>
              <div className="mp-compare-grid">
                {Object.entries(comparisonResult)
                  .filter(([key]) => !key.startsWith('_'))
                  .map(([key, data]) => {
                    const isBest = comparisonResult._bestMarketplace === key;
                    return (
                      <div key={key} className={`mp-compare-card ${isBest ? 'best' : ''}`}>
                        <div className="mp-compare-header">
                          <div className="mp-summary-icon">{data.marketplace?.icon}</div>
                          <div className="mp-summary-name" style={{ fontSize: '18px' }}>
                            {data.marketplace?.name}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            Komisyon: {formatPercent((data.marketplace?.commissionRate || 0) * 100)}
                          </div>
                        </div>
                        <div className="mp-compare-body">
                          <div className="mp-result-row">
                            <span className="mp-result-label">Satış Fiyatı</span>
                            <span className="mp-result-value">
                              {formatCurrency(data.calculatedSalePrice || data.salePrice)}
                            </span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Komisyon</span>
                            <span className="mp-result-value negative">
                              −{formatCurrency(data.commissionAmount)}
                            </span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Reklam</span>
                            <span className="mp-result-value negative">
                              −{formatCurrency(data.adCost)}
                            </span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">İade Etkisi</span>
                            <span className="mp-result-value negative">
                              −{formatCurrency(data.returnImpact)}
                            </span>
                          </div>
                          {data.withholdingAmount > 0 && (
                            <div className="mp-result-row">
                              <span className="mp-result-label">Stopaj</span>
                              <span className="mp-result-value negative">
                                −{formatCurrency(data.withholdingAmount)}
                              </span>
                            </div>
                          )}
                          <div className="mp-result-row mp-result-total">
                            <span className="mp-result-label" style={{ fontWeight: '700' }}>Net Kâr</span>
                            <span className={`mp-result-value ${data.isProfitable ? 'positive' : 'negative'}`} style={{ fontSize: '20px' }}>
                              {formatCurrency(data.netProfit)}
                            </span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Kâr Marjı</span>
                            <span className={`mp-result-value ${data.profitMargin >= 0 ? 'positive' : 'negative'}`}>
                              {formatPercent(data.profitMargin)}
                            </span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Break-Even</span>
                            <span className="mp-result-value neutral">
                              {formatCurrency(data.breakEvenPrice)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Grafik */}
              {chartData.length > 0 && (
                <div className="mp-chart-container mp-animate-in">
                  <h3 className="mp-chart-title">📊 Pazaryeri Karşılaştırma Grafiği</h3>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                      <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          background: 'var(--card-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '12px',
                          fontSize: '13px',
                        }}
                        formatter={(value) => [formatCurrency(value)]}
                      />
                      <Legend />
                      <Bar dataKey="netProfit" name="Net Kâr" radius={[8, 8, 0, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.netProfit >= 0 ? '#059669' : '#dc2626'}
                            opacity={entry.isBest ? 1 : 0.7}
                          />
                        ))}
                      </Bar>
                      <Bar dataKey="commissionAmount" name="Komisyon" fill="#f59e0b" radius={[8, 8, 0, 0]} opacity={0.7} />
                      <Bar dataKey="adCost" name="Reklam" fill="#06C167" radius={[8, 8, 0, 0]} opacity={0.7} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          ) : (
            <div className="mp-card">
              <div className="mp-empty-state">
                <div className="mp-empty-icon">📊</div>
                <div className="mp-empty-title">Karşılaştırma Yapılmadı</div>
                <div className="mp-empty-text">
                  Önce "Fiyat Hesapla" sekmesinde maliyet bilgilerini girin ve "Tümüyle Karşılaştır" butonuna tıklayın.
                </div>
                <button
                  className="mp-btn mp-btn-primary"
                  style={{ marginTop: '20px' }}
                  onClick={() => setActiveTab('calculator')}
                >
                  🧮 Hesaplamaya Git
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Kayıtlı Senaryolar */}
      {activeTab === 'scenarios' && (
        <div className="mp-animate-in">
          {savedScenarios.length > 0 ? (
            <div className="mp-card">
              <div className="mp-table-container">
                <table className="mp-table">
                  <thead>
                    <tr>
                      <th>Ürün</th>
                      <th>Pazaryeri</th>
                      <th>Satış Fiyatı</th>
                      <th>Net Kâr</th>
                      <th>Kâr Marjı</th>
                      <th>Tarih</th>
                      <th>Not</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedScenarios
                      .sort((a, b) => {
                        const dateA = a.createdAt?.toDate?.() || a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(0);
                        const dateB = b.createdAt?.toDate?.() || b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(0);
                        return dateB - dateA;
                      })
                      .map(scenario => (
                        <tr key={scenario.id}>
                          <td style={{ fontWeight: '600' }}>{scenario.productName}</td>
                          <td>
                            <span className="mp-badge mp-badge-blue">
                              {scenario.marketplaceName || scenario.marketplace}
                            </span>
                          </td>
                          <td>{formatCurrency(scenario.result?.calculatedSalePrice || scenario.result?.salePrice)}</td>
                          <td>
                            <span className={scenario.result?.isProfitable ? '' : ''} style={{ color: scenario.result?.isProfitable ? '#059669' : '#dc2626', fontWeight: '600' }}>
                              {formatCurrency(scenario.result?.netProfit)}
                            </span>
                          </td>
                          <td>
                            <span className={`mp-badge ${scenario.result?.profitMargin >= 0 ? 'mp-badge-green' : 'mp-badge-red'}`}>
                              {formatPercent(scenario.result?.profitMargin)}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            {scenario.createdAt?.toDate?.()
                              ? scenario.createdAt.toDate().toLocaleDateString('tr-TR')
                              : scenario.createdAt?.seconds
                                ? new Date(scenario.createdAt.seconds * 1000).toLocaleDateString('tr-TR')
                                : '-'}
                          </td>
                          <td style={{ fontSize: '12px', color: 'var(--text-muted)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {scenario.notes || '-'}
                          </td>
                          <td>
                            <button
                              className="mp-btn mp-btn-secondary mp-btn-sm"
                              onClick={() => handleDeleteScenario(scenario.id)}
                              title="Sil"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="mp-card">
              <div className="mp-empty-state">
                <div className="mp-empty-icon">💾</div>
                <div className="mp-empty-title">Kayıtlı Senaryo Yok</div>
                <div className="mp-empty-text">
                  Hesapladığınız senaryoları kaydetmek için "Fiyat Hesapla" sekmesinde hesaplama yapın ve "Kaydet" butonuna tıklayın.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Kampanya */}
      {activeTab === 'campaign' && (
        <div className="mp-animate-in">
          <div className="mp-calc-panel">
            <div className="mp-calc-header">
              <div>
                <h3 className="mp-card-title" style={{ margin: 0 }}>
                  🎪 Kampanya Fiyatlandırma
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  Kampanya senaryolarının kâr etkisini analiz edin
                </p>
              </div>
            </div>

            <div className="mp-calc-body">
              {/* Kampanya Tipi Seçimi */}
              <div className="mp-campaign-type-grid">
                <div
                  className={`mp-campaign-type-card ${campaignType === 'buyXpayY' ? 'active' : ''}`}
                  onClick={() => { setCampaignType('buyXpayY'); setCampaignResult(null); }}
                >
                  <div className="mp-campaign-type-icon">🛒</div>
                  <div className="mp-campaign-type-name">N Al M Öde</div>
                  <div className="mp-campaign-type-desc">Örn: 4 al 3 öde</div>
                </div>
                <div
                  className={`mp-campaign-type-card ${campaignType === 'percentDiscount' ? 'active' : ''}`}
                  onClick={() => { setCampaignType('percentDiscount'); setCampaignResult(null); }}
                >
                  <div className="mp-campaign-type-icon">🏷️</div>
                  <div className="mp-campaign-type-name">% İndirim + PY Katkısı</div>
                  <div className="mp-campaign-type-desc">Örn: %10 indirim, %2 PY karşılamalı</div>
                </div>
              </div>

              <div className="mp-calc-grid">
                {/* Sol: Kampanya Formu */}
                <div>
                  {/* Pazaryeri Seçimi */}
                  <div className="mp-form-group">
                    <label className="mp-form-label">Pazaryeri</label>
                    <select
                      className="mp-form-select"
                      value={selectedMarketplace}
                      onChange={(e) => handleMarketplaceChange(e.target.value)}
                    >
                      {marketplaces.map(mp => (
                        <option key={mp.id} value={mp.id}>{mp.icon} {mp.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Satış Fiyatı */}
                  <div className="mp-form-group">
                    <label className="mp-form-label">Liste Satış Fiyatı (KDV Dahil) *</label>
                    <div className="mp-form-input-group">
                      <input
                        type="number"
                        className="mp-form-input"
                        placeholder="0.00"
                        value={formData.salePrice}
                        onChange={(e) => updateForm('salePrice', e.target.value)}
                      />
                      <span className="mp-form-suffix">₺</span>
                    </div>
                  </div>

                  {/* Kampanya Parametreleri */}
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', margin: '20px 0 12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    🎯 Kampanya Parametreleri
                  </div>

                  {campaignType === 'buyXpayY' ? (
                    <div className="mp-form-row">
                      <div className="mp-form-group">
                        <label className="mp-form-label">Kaç Al *</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="4"
                            min="2"
                            value={campaignData.buyQuantity}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, buyQuantity: e.target.value }))}
                          />
                          <span className="mp-form-suffix">adet</span>
                        </div>
                      </div>
                      <div className="mp-form-group">
                        <label className="mp-form-label">Kaç Öde *</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="3"
                            min="1"
                            value={campaignData.payQuantity}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, payQuantity: e.target.value }))}
                          />
                          <span className="mp-form-suffix">adet</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mp-form-row">
                      <div className="mp-form-group">
                        <label className="mp-form-label">Toplam İndirim *</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="10"
                            value={campaignData.discountRate}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, discountRate: e.target.value }))}
                          />
                          <span className="mp-form-suffix">%</span>
                        </div>
                      </div>
                      <div className="mp-form-group">
                        <label className="mp-form-label">Pazaryeri Katkısı *</label>
                        <div className="mp-form-input-group">
                          <input
                            type="number"
                            className="mp-form-input"
                            placeholder="2"
                            value={campaignData.marketplaceContribution}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, marketplaceContribution: e.target.value }))}
                          />
                          <span className="mp-form-suffix">%</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tahmini Satış Adedi */}
                  <div className="mp-form-group">
                    <label className="mp-form-label">Kampanya Dönemi Tahmini Satış</label>
                    <div className="mp-form-input-group">
                      <input
                        type="number"
                        className="mp-form-input"
                        placeholder="100"
                        value={campaignData.estimatedSales}
                        onChange={(e) => setCampaignData(prev => ({ ...prev, estimatedSales: e.target.value }))}
                      />
                      <span className="mp-form-suffix">adet</span>
                    </div>
                  </div>

                  {/* Platform Parametreleri */}
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', margin: '20px 0 12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Platform & Maliyet
                  </div>

                  <div className="mp-form-row">
                    <div className="mp-form-group">
                      <label className="mp-form-label">Komisyon Oranı</label>
                      <div className="mp-form-input-group">
                        <input
                          type="number"
                          className="mp-form-input"
                          value={formData.commissionRate}
                          onChange={(e) => updateForm('commissionRate', e.target.value)}
                        />
                        <span className="mp-form-suffix">%</span>
                      </div>
                    </div>
                    <div className="mp-form-group">
                      <label className="mp-form-label">Platform Hizmet Bedeli</label>
                      <div className="mp-form-input-group">
                        <input
                          type="number"
                          className="mp-form-input"
                          value={formData.platformServiceFee}
                          onChange={(e) => updateForm('platformServiceFee', e.target.value)}
                        />
                        <span className="mp-form-suffix">₺</span>
                      </div>
                    </div>
                  </div>

                  {/* Maliyet */}
                  <div className="mp-form-row">
                    <div className="mp-form-group">
                      <label className="mp-form-label">Ürün Maliyeti (Toplam) *</label>
                      <div className="mp-form-input-group">
                        <input
                          type="number"
                          className="mp-form-input"
                          placeholder="0.00"
                          value={formData.rawMaterialCost}
                          onChange={(e) => updateForm('rawMaterialCost', e.target.value)}
                        />
                        <span className="mp-form-suffix">₺</span>
                      </div>
                    </div>
                    <div className="mp-form-group">
                      <label className="mp-form-label">Kargo</label>
                      <div className="mp-form-input-group">
                        <input
                          type="number"
                          className="mp-form-input"
                          value={formData.shippingCost}
                          onChange={(e) => updateForm('shippingCost', e.target.value)}
                        />
                        <span className="mp-form-suffix">₺</span>
                      </div>
                    </div>
                  </div>

                  <div className="mp-form-row">
                    <div className="mp-form-group">
                      <label className="mp-form-label">Reklam Oranı</label>
                      <div className="mp-form-input-group">
                        <input
                          type="number"
                          className="mp-form-input"
                          value={formData.adSpendRate}
                          onChange={(e) => updateForm('adSpendRate', e.target.value)}
                        />
                        <span className="mp-form-suffix">%</span>
                      </div>
                    </div>
                    <div className="mp-form-group">
                      <label className="mp-form-label">İade Oranı</label>
                      <div className="mp-form-input-group">
                        <input
                          type="number"
                          className="mp-form-input"
                          value={formData.returnRate}
                          onChange={(e) => updateForm('returnRate', e.target.value)}
                        />
                        <span className="mp-form-suffix">%</span>
                      </div>
                    </div>
                  </div>

                  {/* Hesapla Butonu */}
                  <button
                    className="mp-btn mp-btn-primary"
                    style={{ width: '100%', marginTop: '20px', justifyContent: 'center' }}
                    onClick={() => {
                      const mp = getMarketplaceParams(selectedMarketplace);
                      const params = {
                        campaignType,
                        salePrice: parseFloat(formData.salePrice) || 0,
                        rawMaterialCost: parseFloat(formData.rawMaterialCost) || 0,
                        laborCost: parseFloat(formData.laborCost) || 0,
                        packagingCost: parseFloat(formData.packagingCost) || 0,
                        subcontractingCost: parseFloat(formData.subcontractingCost) || 0,
                        customCosts: parseFloat(formData.customCosts) || 0,
                        shippingCost: parseFloat(formData.shippingCost) || 0,
                        commissionRate: (parseFloat(formData.commissionRate) || 0) / 100,
                        fixedFee: parseFloat(formData.platformServiceFee) || 0,
                        adSpendRate: (parseFloat(formData.adSpendRate) || 0) / 100,
                        adSpendFixed: parseFloat(formData.adSpendFixed) || 0,
                        returnRate: (parseFloat(formData.returnRate) || 0) / 100,
                        returnShippingCost: parseFloat(formData.returnShippingCost) || parseFloat(formData.shippingCost) || 0,
                        vatRate: (parseFloat(formData.vatRate) || 20) / 100,
                        productVatRate: (parseFloat(formData.productVatRate) || 20) / 100,
                        exchangeRate: parseFloat(formData.exchangeRate) || 1,
                        productVatIncluded: formData.productVatIncluded === 'true',
                        shippingVatIncluded: formData.shippingVatIncluded === 'true',
                        withholdingRate: (parseFloat(formData.withholdingRate) || 0) / 100,
                        buyQuantity: parseInt(campaignData.buyQuantity) || 4,
                        payQuantity: parseInt(campaignData.payQuantity) || 3,
                        discountRate: parseFloat(campaignData.discountRate) || 10,
                        marketplaceContribution: parseFloat(campaignData.marketplaceContribution) || 2,
                        estimatedSales: parseInt(campaignData.estimatedSales) || 100,
                      };
                      const result = calculateCampaignPricing(params);
                      setCampaignResult(result);
                    }}
                    disabled={!formData.salePrice || !formData.rawMaterialCost}
                  >
                    🎪 Kampanya Hesapla
                  </button>
                </div>

                {/* Sağ: Kampanya Sonuçları */}
                <div>
                  {campaignResult ? (
                    <div className="mp-campaign-results mp-animate-in">
                      {/* Kampanya Özet Başlık */}
                      <div className="mp-campaign-summary-header">
                        <div className="mp-campaign-badge">
                          {campaignType === 'buyXpayY' ? '🛒' : '🏷️'} {campaignResult.campaignLabel}
                        </div>
                        <div className={`mp-profit-indicator ${campaignResult.isCampaignProfitable ? 'profit' : 'loss'}`}>
                          {campaignResult.isCampaignProfitable ? '✅ Kârlı' : '❌ Zararda'}
                        </div>
                      </div>

                      {/* Efektif Fiyat Bilgisi */}
                      <div className="mp-campaign-price-bar">
                        <div className="mp-campaign-price-item">
                          <span className="mp-campaign-price-label">Liste Fiyatı</span>
                          <span className="mp-campaign-price-value">{formatCurrency(campaignResult.normal.salePrice)}</span>
                        </div>
                        <div className="mp-campaign-price-arrow">→</div>
                        <div className="mp-campaign-price-item">
                          <span className="mp-campaign-price-label">Efektif Fiyat</span>
                          <span className="mp-campaign-price-value" style={{ color: '#d97706' }}>{formatCurrency(campaignResult.effectiveUnitPrice)}</span>
                        </div>
                        <div className="mp-campaign-price-discount">
                          −{formatPercent(campaignResult.sellerDiscountPct)} satıcı
                          {campaignResult.marketplaceDiscountPct > 0 && (
                            <span> | −{formatPercent(campaignResult.marketplaceDiscountPct)} PY</span>
                          )}
                        </div>
                      </div>

                      {/* Birim Kâr Karşılaştırması */}
                      <div className="mp-campaign-compare-grid">
                        {/* Normal */}
                        <div className="mp-campaign-compare-card">
                          <div className="mp-campaign-compare-title">📋 Kampanyasız</div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Satış</span>
                            <span className="mp-result-value">{formatCurrency(campaignResult.normal.salePrice)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Maliyet</span>
                            <span className="mp-result-value negative">−{formatCurrency(campaignResult.normal.totalProductCost)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Komisyon</span>
                            <span className="mp-result-value negative">−{formatCurrency(campaignResult.normal.commissionAmount)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Kargo</span>
                            <span className="mp-result-value negative">−{formatCurrency(campaignResult.normal.shippingExpense)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Reklam</span>
                            <span className="mp-result-value negative">−{formatCurrency(campaignResult.normal.adCost)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Net KDV</span>
                            <span className={`mp-result-value ${campaignResult.normal.netVatToPay > 0 ? 'negative' : 'positive'}`}>
                              {campaignResult.normal.netVatToPay > 0 ? '−' : '+'}{formatCurrency(Math.abs(campaignResult.normal.netVatToPay))}
                            </span>
                          </div>
                          <div className="mp-campaign-compare-profit">
                            <span>Birim Net Kâr</span>
                            <span style={{ color: campaignResult.normal.netProfit >= 0 ? '#059669' : '#dc2626' }}>
                              {formatCurrency(campaignResult.normal.netProfit)}
                            </span>
                          </div>
                        </div>

                        {/* Kampanyalı */}
                        <div className="mp-campaign-compare-card campaign">
                          <div className="mp-campaign-compare-title">🎪 Kampanyalı</div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Satış</span>
                            <span className="mp-result-value">{formatCurrency(campaignResult.campaign.salePrice)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Maliyet</span>
                            <span className="mp-result-value negative">−{formatCurrency(campaignResult.campaign.totalProductCost)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Komisyon</span>
                            <span className="mp-result-value negative">−{formatCurrency(campaignResult.campaign.commissionAmount)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Kargo</span>
                            <span className="mp-result-value negative">−{formatCurrency(campaignResult.campaign.shippingExpense)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Reklam</span>
                            <span className="mp-result-value negative">−{formatCurrency(campaignResult.campaign.adCost)}</span>
                          </div>
                          <div className="mp-result-row">
                            <span className="mp-result-label">Net KDV</span>
                            <span className={`mp-result-value ${campaignResult.campaign.netVatToPay > 0 ? 'negative' : 'positive'}`}>
                              {campaignResult.campaign.netVatToPay > 0 ? '−' : '+'}{formatCurrency(Math.abs(campaignResult.campaign.netVatToPay))}
                            </span>
                          </div>
                          <div className="mp-campaign-compare-profit">
                            <span>Birim Net Kâr</span>
                            <span style={{ color: campaignResult.campaign.netProfit >= 0 ? '#059669' : '#dc2626' }}>
                              {formatCurrency(campaignResult.campaign.netProfit)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Fark Göstergesi */}
                      <div className={`mp-campaign-diff-banner ${campaignResult.unitProfitDifference >= 0 ? 'positive' : 'negative'}`}>
                        <div className="mp-campaign-diff-icon">
                          {campaignResult.unitProfitDifference >= 0 ? '📈' : '📉'}
                        </div>
                        <div className="mp-campaign-diff-text">
                          <strong>Birim Kâr Farkı:</strong> {campaignResult.unitProfitDifference >= 0 ? '+' : ''}{formatCurrency(campaignResult.unitProfitDifference)}
                        </div>
                      </div>

                      {/* Toplam Etki */}
                      <div className="mp-campaign-total-impact">
                        <h4 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                          📊 Toplam Dönem Etkisi ({campaignResult.estimatedSales} adet)
                        </h4>
                        <div className="mp-campaign-total-grid">
                          <div className="mp-campaign-total-item">
                            <div className="mp-campaign-total-label">Kampanyasız Toplam Kâr</div>
                            <div className="mp-campaign-total-value" style={{ color: campaignResult.normalTotalProfit >= 0 ? '#059669' : '#dc2626' }}>
                              {formatCurrency(campaignResult.normalTotalProfit)}
                            </div>
                          </div>
                          <div className="mp-campaign-total-item">
                            <div className="mp-campaign-total-label">Kampanyalı Toplam Kâr</div>
                            <div className="mp-campaign-total-value" style={{ color: campaignResult.campaignTotalProfit >= 0 ? '#059669' : '#dc2626' }}>
                              {formatCurrency(campaignResult.campaignTotalProfit)}
                            </div>
                          </div>
                          <div className="mp-campaign-total-item highlight">
                            <div className="mp-campaign-total-label">Toplam Kâr Farkı</div>
                            <div className="mp-campaign-total-value" style={{ color: campaignResult.totalProfitDifference >= 0 ? '#059669' : '#dc2626', fontSize: '22px' }}>
                              {campaignResult.totalProfitDifference >= 0 ? '+' : ''}{formatCurrency(campaignResult.totalProfitDifference)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mp-result-panel" style={{ textAlign: 'center', padding: '48px 24px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎪</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                        Kampanya Analizi Bekliyor
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '300px', margin: '0 auto' }}>
                        Kampanya tipini seçin, fiyat ve maliyet bilgilerini girin, ardından "Kampanya Hesapla" butonuna tıklayın.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Senaryo Kaydetme Modalı */}
      {saveModalOpen && (
        <div className="mp-modal-overlay" onClick={() => setSaveModalOpen(false)}>
          <div className="mp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mp-modal-header">
              <h3 className="mp-modal-title">💾 Senaryoyu Kaydet</h3>
              <button className="mp-modal-close" onClick={() => setSaveModalOpen(false)}>✕</button>
            </div>
            <div className="mp-modal-body">
              <div className="mp-form-group">
                <label className="mp-form-label">Ürün</label>
                <input
                  type="text"
                  className="mp-form-input"
                  value={selectedProduct?.name || 'Manuel Hesaplama'}
                  disabled
                />
              </div>
              <div className="mp-form-group">
                <label className="mp-form-label">Pazaryeri</label>
                <input
                  type="text"
                  className="mp-form-input"
                  value={`${activeMP.icon} ${activeMP.name}`}
                  disabled
                />
              </div>
              <div className="mp-form-group">
                <label className="mp-form-label">Net Kâr</label>
                <input
                  type="text"
                  className="mp-form-input"
                  value={formatCurrency(calcResult?.netProfit)}
                  disabled
                  style={{ color: calcResult?.isProfitable ? '#059669' : '#dc2626', fontWeight: '600' }}
                />
              </div>
              <div className="mp-form-group">
                <label className="mp-form-label">Not (Opsiyonel)</label>
                <input
                  type="text"
                  className="mp-form-input"
                  placeholder="Bu senaryo hakkında bir not ekleyin..."
                  value={scenarioNote}
                  onChange={(e) => setScenarioNote(e.target.value)}
                />
              </div>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn mp-btn-secondary" onClick={() => setSaveModalOpen(false)}>
                İptal
              </button>
              <button className="mp-btn mp-btn-primary" onClick={handleSaveScenario}>
                💾 Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketplacePricing;
